import { useCallback } from 'react';
import { useAdminEventStream } from '@/shared/lib/sse/useEventStream';
import type { SseEvent } from '@/shared/lib/sse/parser';
import { useAuthStore } from '@/shared/lib/auth/store';
import { fetchMe } from '../api/me';

/* Etap 35 slice 6 — the frontend half of "revoke applies without re-login".
 *
 * The backend already enforces this for real (RequirePermission<P> re-reads
 * the DB on every API call — see admin_rbac_e2e.rs's
 * revoked_permission_blocks_next_request_without_relogin). Without this
 * hook, the UI would still be stuck showing stale nav items / pages until
 * the admin manually refreshes or re-logs in — exactly the gap both
 * reference systems' (Гоблин, ФлитПро) frontends shipped with.
 *
 * On `admin_permissions_changed` (published by roles::update when a role's
 * grants change, or admins::update when an admin's role is reassigned — see
 * EventKind doc comment), if the event is about the CURRENT admin, re-fetch
 * `/v1/me` and push it into the auth store. Every `usePermission()` call and
 * `PermissionGate` re-renders reactively off that store update — no
 * navigation, no reload, no re-login.
 *
 * Mount once for the whole authenticated admin session (AppShell), same
 * pattern as the existing chat-unread SSE listener. */
export function useLivePermissionSync(): void {
  const onEvent = useCallback((ev: SseEvent) => {
    if (ev.event !== 'admin_permissions_changed') return;
    let adminId: unknown;
    try {
      adminId = (JSON.parse(ev.data) as { admin_id?: unknown }).admin_id;
    } catch {
      return;
    }
    const me = useAuthStore.getState().user;
    if (!me || adminId !== me.user_id) return;
    void fetchMe()
      .then((fresh) => useAuthStore.getState().setUser(fresh))
      .catch(() => {
        // best-effort — the next navigation/refresh will pick up the change anyway
      });
  }, []);
  useAdminEventStream(onEvent);
}
